<template>
    <v-menu
      :disabled="disableFilterMenuBox"
      v-model="menuOpened"
      :close-on-content-click="false"
      offset-y
      content-class="filter-menu-container"
      @click:outside="handleClickOutsideMenu"
    >
        <!-- Menu Activator -->
      <template v-slot:activator="{ on, attrs }">
        <button
          :disabled="disableFilterMenuBox"
          :class="menuOpened ? 'filter-menu-activator open' : 'filter-menu-activator'"
          v-bind="attrs"
          v-on="on"
          @click="handleMenuToggle"
        >
          {{ (name === 'calls' || type === 'calls') ? 'Files' : (name ? name : type) | formatName }}
          <span v-if="selectedItems.length > 0" class="selected-items-indicator">{{ selectedItems.length }}</span>
          <i :class="menuOpened ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </button>
      </template>

      <!-- Filter Menu Content -->
       <div class="filter-menu-results-container" ref="filterMenu">
        <IncludeMenu v-if="type=='tags'" ref="includeMenuRef"/>
        <!-- filer option search -->
        <div class="filter-menu-search" v-if="!isMetadataFilter && type !== 'time'">
            <input v-model="itemSearched" type="text" :placeholder="`Search ${type}`" />
        </div>
        <!-- filter options items -->
        <div class="filter-menu-results">
          <!-- Wrap metadata filter items in v-menu -->
          <div v-for="(item, itemIndex) in searchedItems" :key="item.id">
            <v-menu
              v-if="isMetadataFilter"
              :close-on-content-click="false"
              offset-x
              right
              :nudge-right="0"
              :nudge-width="100"
              content-class="filter-submenu-container"
              :value="activeSubmenuId === `${item.id}-${itemIndex}`"
            >
              <template v-slot:activator="{ on, attrs }">
                <div 
                  class="filter-menu-item-container"
                  v-bind="attrs"
                  v-on="on"
                  @mouseenter="handleSubmenuItemEnter(`${item.id}-${itemIndex}`)"
                  @mouseleave="handleSubmenuItemLeave()"
                >
                  <div class="filter-menu-item">
                    <div class="menu-item-content">
                      <div class="item-main-content">
                        {{item.name}}
                        <!-- <i class="ri-arrow-right-s-line"></i> -->
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <div
                @mouseenter="handleSubmenuEnter()"
                @mouseleave="handleSubmenuLeave()"
              >
                <div class="submenu-list">
                  <div
                    v-for="(subItem, index) in getSortedSubItems(item.values)"
                    :key="index"
                    @click="handleSubmenuSelect(item, subItem)"
                    class="submenu-list-item"
                  >
                    <span class="submenu-item-title">
                      <!-- Prevent click event from bubbling to parent div to avoid double-triggering handleSubmenuSelect -->
                      <input 
                        type="checkbox"
                        :checked="selectedItems.some(selectedItem => 
                          selectedItem.name === item.name && 
                          selectedItem.value === subItem &&
                          selectedItem.id === item.document_id
                        )"
                        @click.stop
                        @change="handleSubmenuSelect(item, subItem)"
                      />
                      {{ subItem }}
                    </span>
                  </div>
                </div>
              </div>
            </v-menu>

            <!-- Regular non-metadata filter items -->
            <div 
              v-else
              @click.self="!isMetadataFilter && handleSelect(item)" 
              class="filter-menu-item-container"
            >
              <!-- Rest of the non-metadata filter item content -->
              <div class="filter-menu-item">
                <div 
                  class="menu-item-content"
                  @click="handleSelect(item)"
                >
                  <div class="item-main-content">
                    <input 
                      :type="type === 'time' ? 'radio' : 'checkbox'"
                      :name="type === 'time' ? 'timeFilter' : ''"
                      :checked="selectedItems.map(i=>i.id).includes(item.id)" 
                    />
                    <template v-if="type=='projects'">
                      <template v-if="item.isSampleUnassigned">
                        {{ item.emoji }} {{ item.projectDisplayName }}
                      </template>
                      <template v-else>
                        {{ item.emoji }} {{ item.name }}
                      </template>
                    </template>
                    <template v-if="type=='questions' || type=='calls'"> {{item.name}} </template>
                    <template v-if="type==='tags'">
                      <DisplayTag :tag="item" :spaceOutTags="false" />
                    </template>
                    <template v-if="type==='creators' || type==='time' || type==='fileType'"> {{item.name}} </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-menu>
</template>

<script>
import DisplayTag from '@/components/Tags/DisplayTag.vue';
import IncludeMenu from './IncludeMenu.vue';

export default {
  components:{ DisplayTag, IncludeMenu },
  name: 'FilterMenuBox',
  data() {
    return {
        itemSearched: '',
        selectedItems: [],
        menuOpened: false,
        filterType: "any_of",
        i:0,
        previousSelectedItems:[],
        // Submenu state for smooth interaction
        activeSubmenuId: null,
        submenuCloseTimeout: null,
        submenuOpenTimeout: null,
        submenuOpenDelay: 200,
        submenuCloseDelay: 300
    }
  },
  props: {
    type: {
      required: true
    },
    data: {
      required: true
    },
    name: {
      required: true,
      default: null
    },
    disableFilterMenuBox:{
      default: false
    }

  },
  computed: {
        searchedItems() {
          const filteredData = this.data
            .filter(item => 
              item.name && item.name.toLowerCase().includes(this.itemSearched.toLowerCase())
            );
          
          // Skip sorting for time and projects filters to preserve original order
          if (this.type === 'time' || this.type === 'projects') {
            return filteredData;
          }
          
          // Sort alphabetically by name for other filters
          return filteredData.sort((a, b) => {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          });
        },
    
        // if the filter isn't questions, calls, tags, projects or creators then its a metadata filter menu
        isMetadataFilter() {
          const excludedFilterTypes = ['questions', 'calls', 'tags', 'projects', 'creators', 'fileType', 'time'];
          return !excludedFilterTypes.includes(this.type);
        },
        // Use a computed property to get the selected items
        computedSelectedItems() {
          return this.selectedItems.slice(); // Create a shallow copy of the selectedItems array
        },
  },
  methods: {
    getSortedSubItems(values) {
      // Sort submenu items alphabetically
      if (!values || !Array.isArray(values)) return [];
      return [...values].sort((a, b) => {
        return a.toLowerCase().localeCompare(b.toLowerCase());
      });
    },
    toggleMenu() {
      console.log('toggleMenu called')
      this.menuOpened = !this.menuOpened;
    },
    onClickOutside() {
      console.log('onClickOutside called')
      this.menuOpened = false;
    },
    arraysAreEqual(arr1, arr2) {
        if (arr1.length !== arr2.length) {
          return false;
        }
        for (let i = 0; i < arr1.length; i++) {
          if (arr1[i] !== arr2[i]) {
            return false;
          }
        }
        return true;
    },
    sortItems() {
      // Sort the items with selectedItems at the top when the menu is closed
      const selectedItems = this.selectedItems.slice();
      const remainingItems = this.searchedItems.filter(
        (item) => !selectedItems.some((selectedItem) => selectedItem.id === item.id)
      );
      this.sortedItems = selectedItems.concat(remainingItems);
    },
    handleSelect(item) {
        // For time filter, clear previous selection since it's radio button
        if (this.type === 'time') {
          // Check if this item exists in the array already, if it does remove it
          if(this.selectedItems.includes(item)) this.selectedItems = []
          else this.selectedItems = [item]; // Replace array instead of clearing then pushing
        } else {
          // Existing logic for other filter types
          const isSelected = this.selectedItems.some(selectedItem => {
            if ('value' in item) {
              return selectedItem.name === item.name && selectedItem.value === item.value;
            } else {
              return selectedItem.id === item.id;
            }
          });

          if (isSelected) {
            this.selectedItems = this.selectedItems.filter(selectedItem => {
              if ('value' in item) {
                return !(selectedItem.name === item.name && selectedItem.value === item.value);
              } else {
                return selectedItem.id !== item.id;
              }
            });
          } else {
            this.selectedItems.push(item);
          }
        }
      },
    
    handleMenuToggle(){
      console.log('handleMenuToggle called')
      const val = !this.menuOpened 
      this.menuOpened = val
      if(val==false) this.sortItems()
      if(this.arraysAreEqual(this.previousSelectedItems,this.selectedItems)) return;
      if(this.selectedItems.length<=0){
        console.log("removing filters")
        if(val==false) this.$emit('remove-filter',this.type)
        return;
      }
      
      const _selected = this.selectedItems.map((item) =>{
        // This condition is important cause for calls filter item.name is required in backend while for others only item.id is needed
        if(this.type == 'calls') return item
        // Return whole metadata filter object, cause the ai_search endpoints need it as it is. eg: {"id": "UWHDhI9kSyyGvjDyAslD","name": "country","value": "new value"}
        if(this.type == 'metadata') return item
        // for other filters only id is needed
        return {
          "id" : item.id
        }
      })
      const _filterType = this.type == 'tags' ? this.$refs.includeMenuRef.include : 'any_of'
      if(this.type == 'tags' && val==false){
        console.log("Tags menu closed")
        if(this.selectedItems.length>0){
          console.log("Apply tags filter")
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'projects' && val==false){
        console.log("Projects menu closed. Apply projects filter")
        if(this.selectedItems.length>0){
          console.log("Apply projects filter")
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'questions' && val==false){
        console.log("Questions menu closed. Apply projects filter")
        if(this.selectedItems.length>0){
          console.log("Apply questions filter")
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'calls' && val==false){
        console.log("Calls menu closed. Apply projects filter")
        if(this.selectedItems.length>0){
          console.log("Apply calls filter",this.type,_selected,_filterType)
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'metadata' && val==false){
        console.log("metadata menu closed. Apply metadata filter")
        if(this.selectedItems.length>0){
          console.log("Apply metadata filter",this.type,_selected,_filterType)
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'creators' && val==false){
        console.log("Creators menu closed")
        if(this.selectedItems.length>0){
          console.log("Apply creators filter")
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
      if(this.type == 'time' && val==false){
        console.log("Time menu closed")
        if(this.selectedItems.length>0){
          console.log("Apply time filter")
          this.$emit('apply-filter',this.type,_selected,'exact') // Time filter always uses 'exact' match
        }
      }
      if(this.type == 'fileType' && val==false){
        console.log("File type menu closed")
        if(this.selectedItems.length>0){
          console.log("Apply file type filter")
          this.$emit('apply-filter',this.type,_selected,_filterType)
        }
      }
    },

    // close filter menu when clicked outside the filter
    handleClickOutsideMenu(event) {
      if (this.menuOpened && !this.$refs.filterMenu.contains(event.target) && !this.$el.contains(event.target)) {
        //toggle menu
        this.handleMenuToggle()
      }
    },
    handleSubmenuSelect(parentItem, subItem) {
      this.handleSelect({
        id: parentItem.document_id,
        name: parentItem.name,
        value: subItem
      });
    },
    
    /* ===== SUBMENU DELAY LOGIC FOR SMOOTH INTERACTION =====
     * This delay logic ensures smooth submenu interactions by:
     * 1. Keeping submenus open for 300ms after cursor moves to them
     * 2. Preventing accidental submenu switches when passing over other items
     * 3. Allowing re-hovering the same item to keep its submenu open
     * 4. Opening submenus immediately on first hover for responsive feel
     * 5. Keeping submenus open while moving within them
     * 6. Closing submenus after 300ms of cursor leaving
     * 7. Preventing conflicting hover actions by clearing timeouts
     * 8. Cleaning up timers when the component is destroyed
     */
    
    // Helper: Clear all pending submenu timeouts
    clearSubmenuTimeouts() {
      if (this.submenuCloseTimeout) {
        clearTimeout(this.submenuCloseTimeout);
        this.submenuCloseTimeout = null;
      }
      if (this.submenuOpenTimeout) {
        clearTimeout(this.submenuOpenTimeout);
        this.submenuOpenTimeout = null;
      }
    },
    
    // Helper: Set active submenu with optional delay
    setActiveSubmenu(itemId, delay = 0) {
      this.clearSubmenuTimeouts();
      if (delay > 0) {
        this.submenuOpenTimeout = setTimeout(() => {
          this.activeSubmenuId = itemId;
        }, delay);
      } else {
        this.activeSubmenuId = itemId;
      }
    },
    
    handleSubmenuItemEnter(itemId) {
      if (this.activeSubmenuId === itemId) {
        this.clearSubmenuTimeouts();
        return;
      }
      const delay = this.activeSubmenuId !== null ? this.submenuOpenDelay : 0;
      this.setActiveSubmenu(itemId, delay);
    },
    
    handleSubmenuItemLeave() {
      this.clearSubmenuTimeouts();
      this.submenuCloseTimeout = setTimeout(() => {
        this.activeSubmenuId = null;
      }, this.submenuCloseDelay);
    },
    
    handleSubmenuEnter() {
      this.clearSubmenuTimeouts();
    },
    
    handleSubmenuLeave() {
      this.submenuCloseTimeout = setTimeout(() => {
        this.activeSubmenuId = null;
      }, this.submenuCloseDelay);
    }
  },
  filters:{
    formatName(term){
      if(term=='time') return 'Date created'
      else if(term=='creators') return 'Creator'
      else if(term=='projects') return 'Project'
      else return term
    }
  },
  mounted(){
    document.addEventListener('click', this.handleClickOutsideMenu)
  },
  beforeDestroy(){
    document.removeEventListener('click', this.handleClickOutsideMenu)
    this.clearSubmenuTimeouts();
  },
  watch: {
    menuOpened(newVal) {
      console.log('Menu state changed:', newVal)
      this.menuOpened = newVal
      console.log(this.menuOpened ? 'Menu is opened' : 'Menu is closed')
    },
    computedSelectedItems(newSelectedItems, oldSelectedItems) {
      // Store the previous state of selectedItems
      this.previousSelectedItems = oldSelectedItems;

      // Your other logic or actions here
      // console.log('New selectedItems:', newSelectedItems);
      // console.log('Previous selectedItems:', oldSelectedItems);
    },
  }
}
</script>

<style scoped>
/* .filter-wrapper {
  position: relative;
  display: inline-block;
} */

.filter-menu-activator {
  display: flex;
  padding: 8px 8px 8px 12px;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid var(--neutrals-200, #EDEDED);
  background: var(--neutrals-white, #FFF);
  margin-left: 12px;
  cursor: pointer;
  color: var(--neutrals-500, #666);
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.12px;
  text-transform: capitalize;
}

.filter-menu-activator:disabled{
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.filter-menu-activator.open{
  border: 1px solid var(--primary-500, #5B7EDB);
  background: var(--primary-200, #F6F8FE);
  color: #5B7EDB;
  transition: all 0.1s;
}

.filter-menu-container {
  min-width: 200px;
  padding: 16px 0px 0px 0px;
  border-radius: 12px;
  border: 1px solid #d2dcfb;
  background: #ffffff;
  box-shadow: 0px 0px 8px 0px #66666626;
  margin-top: 5px;
}

.selected-items-indicator{
  display: flex;
  width: 16px;
  height: 16px;
  padding: 2px 8px;
  justify-content: space-between;
  align-items: center;
  border-radius: 16px;
  background: var(--primary-500, #5B7EDB);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neutrals-white, #FFF);
  leading-trim: both;
  text-edge: cap;
  font-family: Inter;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 16.5px */
  text-transform: capitalize;
  margin-left: 2px;
  margin-right: 2px;
}

.filter-menu-results{
  max-height: 344px;
  overflow-y: scroll;
}

.filter-menu-search{
  display: flex;
  padding: 6px 12px;
  margin:16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  border: 1.5px solid var(--neutrals-200, #EDEDED);
  background: var(--neutrals-white, #FFF);
  color: #4C4C4C;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  width: auto;
  position: sticky;
  top: 0;
  background: #fff;
}

.filter-menu-results{
  margin-bottom: 12px;
}

.filter-menu-item-container{
  margin-bottom: 8px;
  display: flex;
  padding: 6px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  min-width: 200px;
}

.filter-menu-item-container:hover{
  background: #F8F8F8;
  cursor: pointer;
}

.filter-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.menu-item-content {
  cursor: pointer;
}

.item-main-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.submenu-card {
  border-radius: 8px;
  border: 1px solid var(--neutrals-200, #EDEDED);
  background: var(--neutrals-white, #FFF);
  box-shadow: 0px 0px 8px 0px rgba(102, 102, 102, 0.15);
}

.filter-submenu-container {
    min-width: 200px;
    padding: 16px 0px;
    border-radius: 12px;
    border: 1px solid #d2dcfb;
    background: #ffffff;
    box-shadow: 0px 0px 8px 0px #66666626;
    margin-top: 5px;
}

.filter-menu-item {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  text-align: left;
  color: #4C4C4C;
}

.filter-menu-item input, .submenu-list-item input{
  margin-right: 8px;
  cursor: pointer;
  margin-top: 0px;
}

.submenu-list-item {
    padding: 6px 16px;
}

.submenu-list-item .submenu-item-title {
  cursor: pointer;
  padding: 8px 16px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  text-align: left;
  color: #4C4C4C;
}

.submenu-list-item:hover {
  background-color: var(--neutrals-100, #F8F8F8);
}

.submenu-list {
  max-height: 344px;  /* Same as main menu */
  overflow-y: scroll;
}
</style>
